import { validateDiscoveryResponse } from '../03-feed-session.mjs';
import { observeContinuation } from '../ui/26-scroll-continuation.mjs';
import { createMediaViewer } from '../ui/05-media-viewer.mjs';

/** Browse only server-authorized images. Filters describe content, never inferred
 * reader emotion. Each request belongs to one actor, route and generation; leaving
 * the view cancels its presentation and inspection listeners. */
export function createExplore(
  ctx,
  { viewer = createMediaViewer(ctx), platform = globalThis } = {},
) {
  let owner,
    generation = 0,
    controller,
    lastSection,
    remembered = { scroll: 0, height: 0, depth: 0, signature: '' },
    depth = 0,
    stopLayout,
    stopRestore,
    stopPrefetch;
  let filters = { q: '', tag: '', theme: '', color: '', tone: '' };
  function disposeExplore() {
    if (lastSection?.isConnected && ctx.state?.me?.id === owner)
      remembered = {
        scroll: platform.scrollY ?? 0,
        height: lastSection.offsetHeight ?? 0,
        depth,
        signature: JSON.stringify([filters, ctx.contentScope ?? 'ordinary']),
      };
    generation++;
    controller?.abort();
    stopPrefetch?.();
    stopPrefetch = undefined;
    stopLayout?.();
    stopLayout = undefined;
    stopRestore?.();
    stopRestore = undefined;
    viewer.dispose();
  }
  function renderExplore() {
    disposeExplore();
    const actor = ctx.state.me.id;
    const contentScope = ctx.contentScope ?? 'ordinary';
    if (owner !== actor) {
      owner = actor;
      filters = { q: '', tag: '', theme: '', color: '', tone: '' };
      remembered = { scroll: 0, height: 0, depth: 0, signature: '' };
    }
    const restoreTarget =
      remembered.signature === JSON.stringify([filters, ctx.contentScope ?? 'ordinary'])
        ? { ...remembered }
        : { scroll: 0, height: 0, depth: 0 };
    depth = 0;
    const section = ctx.e('section', 'explore-view');
    lastSection = section;
    if (restoreTarget.height && section.style)
      section.style.minHeight = `${restoreTarget.height}px`;
    ctx.renderContentScope?.(section);
    section.append(ctx.e('h1', 'explore-title', ctx.uiText('Explore')));
    const links = ctx.e('div', 'explore-links');
    links.append(
      ctx.link(ctx.uiText('Find people'), '#/discover', 'text-button'),
      ctx.link(ctx.uiText('Choose interest packs'), '#/starter', 'text-button'),
    );
    const form = ctx.e('form', 'explore-search');
    const input = ctx.field('input', {
      type: 'search',
      value: filters.q,
      maxLength: 120,
      placeholder: ctx.uiText('Search images, subjects and ideas'),
      ariaLabel: ctx.uiText('Search images, subjects and ideas'),
    });
    const submit = ctx.e('button', 'icon-button');
    submit.type = 'submit';
    submit.setAttribute('aria-label', ctx.uiText('Search images'));
    submit.append(ctx.icon('search'));
    const filterPanel = ctx.e('div', 'explore-filters');
    const filterToggle = ctx.button(
      '',
      () => {
        filterPanel.hidden = !filterPanel.hidden;
        filterToggle.setAttribute('aria-expanded', String(!filterPanel.hidden));
      },
      'icon-button',
    );
    filterToggle.setAttribute('aria-label', ctx.uiText('Refine images'));
    filterToggle.title = ctx.uiText('Refine images');
    filterToggle.type = 'button';
    filterToggle.append(ctx.icon('ellipsis'));
    filterToggle.setAttribute('aria-expanded', 'false');
    form.append(input, submit, filterToggle);
    filterPanel.hidden = true;
    const applied = ctx.e('div', 'explore-applied');
    const results = ctx.e('div', 'explore-results');
    results.setAttribute('aria-live', 'polite');
    section.append(links, form, applied, filterPanel, results);
    ctx.view.append(section);
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      filters.q = input.value.trim();
      void load();
    });
    function choose(key, value) {
      filters[key] = filters[key] === value ? '' : value;
      void load();
    }
    function renderApplied() {
      applied.replaceChildren();
      for (const [key, value] of Object.entries(filters)) {
        if (!value || key === 'q') continue;
        const chip = ctx.button(`${ctx.uiText(value)} ×`, () => choose(key, value), 'explore-chip');
        chip.setAttribute('aria-label', `${ctx.uiText('Remove filter')}: ${ctx.uiText(value)}`);
        applied.append(chip);
      }
      if (Object.values(filters).some(Boolean))
        applied.append(
          ctx.button(
            'Reset',
            () => {
              filters = { q: '', tag: '', theme: '', color: '', tone: '' };
              input.value = '';
              void load();
            },
            'text-button',
          ),
        );
    }
    function renderFacets(facets = {}) {
      const focused = platform.document?.activeElement?.dataset?.exploreFacet;
      filterPanel.replaceChildren();
      for (const [key, label, facet] of [
        ['tag', 'Subjects', 'tags'],
        ['theme', 'Themes', 'themes'],
        ['color', 'Colors', 'colors'],
        ['tone', 'Mood of image', 'tones'],
      ]) {
        const group = ctx.e('fieldset', 'explore-filter-group');
        group.append(ctx.e('legend', '', ctx.uiText(label)));
        const options = facets[facet] ?? [];
        for (const { value, count } of options.slice(0, 24)) {
          const chip = ctx.button(ctx.uiText(value), () => choose(key, value), 'explore-chip');
          chip.dataset.exploreFacet = `${key}:${value}`;
          chip.setAttribute('aria-pressed', String(filters[key] === value));
          chip.setAttribute('aria-label', `${ctx.uiText(value)} · ${count} ${ctx.uiText('Posts')}`);
          group.append(chip);
        }
        if (options.length) filterPanel.append(group);
      }
      if (!filterPanel.children.length)
        filterPanel.append(
          ctx.e(
            'p',
            'subtle',
            ctx.uiText('More image details will appear as people share tagged photos.'),
          ),
        );
      filterPanel.append(
        ctx.e(
          'p',
          'subtle',
          ctx.uiText(
            'Mood describes the image’s editorial tone. It does not assess your feelings.',
          ),
        ),
      );
      if (focused)
        [...filterPanel.querySelectorAll('[data-explore-facet]')]
          .find((node) => node.dataset.exploreFacet === focused)
          ?.focus({ preventScroll: true });
    }
    let tiles,
      paging,
      nextOffset = null;
    const seen = new Set();
    const pagingStatus = ctx.e('div', 'explore-paging');
    function masonry() {
      stopLayout?.();
      if (!platform.ResizeObserver || !platform.requestAnimationFrame || !tiles.style) return;
      let stopped = false,
        queued = false;
      const arrange = () => {
        queued = false;
        if (stopped || !tiles.isConnected) return;
        const width = tiles.clientWidth;
        if (!width) return;
        const columns = width >= 640 ? 3 : 2,
          gap = 6;
        const itemWidth = (width - gap * (columns - 1)) / columns;
        const heights = Array(columns).fill(0);
        tiles.classList.add('is-masonry');
        for (const tile of tiles.children) {
          const column = heights.indexOf(Math.min(...heights));
          tile.style.width = `${itemWidth}px`;
          tile.style.left = `${column * (itemWidth + gap)}px`;
          tile.style.top = `${heights[column]}px`;
          heights[column] += tile.offsetHeight + gap;
        }
        tiles.style.height = `${Math.max(...heights)}px`;
      };
      const schedule = () => {
        if (!queued && !stopped) {
          queued = true;
          platform.requestAnimationFrame(arrange);
        }
      };
      const observer = new platform.ResizeObserver(schedule);
      observer.observe(tiles);
      for (const tile of tiles.children) observer.observe(tile);
      schedule();
      stopLayout = () => {
        stopped = true;
        observer.disconnect();
      };
    }
    function prefetch(current) {
      stopPrefetch?.();
      stopPrefetch = undefined;
      if (nextOffset === null) return;
      const observer = observeContinuation(pagingStatus, () => load(true), {
        platform,
        active: () =>
          generation === current &&
          ctx.state?.me?.id === actor &&
          ctx.route()[0] === 'explore' &&
          (ctx.contentScope ?? 'ordinary') === contentScope,
        margin: 800,
      });
      stopPrefetch = () => observer.disconnect();
    }
    async function load(append = false, restore = false) {
      if (append && (paging || nextOffset === null)) return;
      stopPrefetch?.();
      stopPrefetch = undefined;
      const current = append ? generation : ++generation;
      let completed = false;
      if (!append) {
        controller?.abort();
        controller = new AbortController();
        nextOffset = null;
        depth = 0;
        stopRestore?.();
        stopRestore = undefined;
        if (!restore && section.style) section.style.minHeight = '';
        seen.clear();
        renderApplied();
        results.replaceChildren(ctx.e('p', 'subtle', ctx.uiText('Finding images…')));
      }
      paging = true;
      const active = () =>
        generation === current &&
        ctx.state?.me?.id === actor &&
        ctx.route()[0] === 'explore' &&
        (ctx.contentScope ?? 'ordinary') === contentScope;
      results.setAttribute('aria-busy', 'true');
      pagingStatus.replaceChildren(ctx.e('p', 'subtle', ctx.uiText('Finding images…')));
      const params = new URLSearchParams({
        scope: 'visual',
        contentScope,
        limit: String(append ? 18 : 6),
      });
      for (const [key, value] of Object.entries(filters)) if (value) params.set(key, value);
      let offset = append ? nextOffset : 0;
      const scrollAtRequest = platform.scrollY ?? 0;
      let userScrolled = false,
        restorationQueued = false;
      const releaseGestures = () => {
        platform.removeEventListener?.('wheel', onUserScroll);
        platform.removeEventListener?.('touchmove', onUserScroll);
        platform.removeEventListener?.('keydown', onUserScroll);
        if (stopRestore === releaseGestures) stopRestore = undefined;
      };
      const onUserScroll = () => {
        userScrolled = true;
      };
      if (restore) {
        stopRestore = releaseGestures;
        platform.addEventListener?.('wheel', onUserScroll, { passive: true });
        platform.addEventListener?.('touchmove', onUserScroll, { passive: true });
        platform.addEventListener?.('keydown', onUserScroll);
      }
      const requestDone = ctx.api.timings?.start(
        append ? 'load-more' : 'first-screen-request',
        'explore',
      );
      const imageDone = !append
        ? ctx.api.timings?.start('first-image-ready', 'explore')
        : undefined;
      const ready = !append ? ctx.api.timings?.start('first-screen-ready', 'explore') : undefined;
      try {
        let response;
        do {
          if (offset) params.set('offset', String(offset));
          else params.delete('offset');
          response = await ctx.api(`/api/search?${params}`, undefined, {
            signal: controller.signal,
          });
          if (!active()) return;
          validateDiscoveryResponse(response, contentScope, ctx.discoveryPolicyVersion);
          requestDone?.();
          renderFacets(response.facets);
          if (!append && offset === 0) tiles = ctx.e('div', 'explore-tiles');
          for (const post of response.posts) {
            const media = [...(post.media ?? [])];
            const legacy = post.image && ctx.photoSource(post.image);
            if (legacy && !media.some((item) => item.kind === 'image' && item.url === legacy))
              media.push({
                kind: 'image',
                url: legacy,
                alt: post.alt,
                aspectRatio: post.imageAspectRatio,
              });
            const inspection = { ...post, media };
            media.forEach((item, index) => {
              if (item.kind !== 'image') return;
              // Explore presents distinct artwork. The first authorized ranked
              // post owns its tile; ordinary feed/text search still shows every note.
              const key = item.url;
              if (seen.has(key)) return;
              seen.add(key);
              const tile = ctx.e('article', 'explore-tile');
              let failed = false;
              const inspect = ctx.button(
                '',
                () => {
                  if (!active()) return;
                  if (failed) ctx.go(`post/${post.id}`);
                  else viewer.open(inspection, index);
                },
                'explore-image',
              );
              inspect.setAttribute(
                'aria-label',
                `${ctx.uiText('View image')}: ${item.alt || post.title || ctx.uiText('Shared photo')}`,
              );
              const image = ctx.field('img', {
                src: ctx.photoSource(item.url, 640, item.variants),
                srcset: ctx.photoSet?.(item.url, item.variants) ?? '',
                sizes: '(max-width:720px) 50vw, 240px',
                fetchPriority: !append && seen.size === 1 ? 'high' : 'auto',
                alt: item.alt || post.alt || '',
                loading: !append && seen.size === 1 ? 'eager' : 'lazy',
                decoding: 'async',
              });
              const validDimension = (value) =>
                Number.isFinite(value) && value >= 1 && value <= 100000;
              if (validDimension(item.width) && validDimension(item.height)) {
                image.width = Math.round(item.width);
                image.height = Math.round(item.height);
              } else {
                const candidate =
                  item.aspectRatio ?? (item.url === legacy ? post.imageAspectRatio : undefined);
                const ratio =
                  Number.isFinite(candidate) && candidate >= 0.01 && candidate <= 100
                    ? candidate
                    : 1.5;
                // Match feed legacy-image reservation; these intrinsic attributes
                // stabilize lazy tiles without cropping the eventual natural image.
                image.width = 1536;
                image.height = Math.max(1, Math.round(1536 / ratio));
              }
              image.addEventListener(
                'error',
                () => {
                  if (!active()) return;
                  failed = true;
                  image.hidden = true;
                  inspect.append(
                    ctx.e(
                      'span',
                      'explore-image-error',
                      ctx.uiText('Image unavailable · Open post'),
                    ),
                  );
                },
                { once: true },
              );
              inspect.append(image);
              tile.append(
                inspect,
                ctx.link(
                  post.title || post.text || ctx.uiText('Open post'),
                  `#/post/${post.id}`,
                  'explore-caption',
                ),
              );
              tiles.append(tile);
            });
          }
          if (!append && offset === 0)
            results.replaceChildren(
              seen.size
                ? tiles
                : ctx.empty(
                    ctx.uiText('No matching images. Try a different subject or reset filters.'),
                  ),
              pagingStatus,
            );
          if (!append && offset === 0) {
            ctx.api.timings?.afterPaint(ready ?? (() => {}));
            ctx.api.timings?.afterImage(imageDone ?? (() => {}), tiles.querySelector?.('img'));
          }
          nextOffset =
            response.postsHasMore &&
            Number.isSafeInteger(response.nextOffset) &&
            response.nextOffset > offset
              ? response.nextOffset
              : null;
          depth = offset;
          masonry();
          offset = nextOffset;
          // Revalidate every previously loaded page on return. Retain only numeric
          // depth/geometry between routes, never an old authorized post snapshot.
        } while (restore && !userScrolled && offset !== null && offset <= restoreTarget.depth);
        pagingStatus.replaceChildren();
        if (response.paginationLimited)
          pagingStatus.append(
            ctx.e(
              'p',
              'subtle',
              ctx.uiText('More images match. Refine your search to find a particular subject.'),
            ),
          );
        if (nextOffset !== null)
          pagingStatus.append(ctx.e('span', 'sr-only', ctx.uiText('Finding images…')));
        if (
          restore &&
          !userScrolled &&
          ((platform.scrollY ?? 0) === scrollAtRequest ||
            (platform.scrollY ?? 0) === restoreTarget.scroll) &&
          restoreTarget.scroll
        ) {
          restorationQueued = true;
          const frame = platform.requestAnimationFrame ?? ((callback) => callback());
          frame(() => {
            if (active() && !userScrolled) {
              if (section.style) section.style.minHeight = '';
              const maximum = platform.document
                ? Math.max(
                    0,
                    (platform.document.documentElement?.scrollHeight ?? 0) -
                      (platform.innerHeight ?? 0),
                  )
                : restoreTarget.scroll;
              platform.scrollTo?.({
                top: Math.min(restoreTarget.scroll, maximum),
                behavior: 'instant',
              });
            } else if (active() && section.style) section.style.minHeight = '';
            releaseGestures();
          });
        }
        if (!restorationQueued && section.style) section.style.minHeight = '';
        ctx.api.timings?.afterPaint(ready ?? (() => {}));
        completed = true;
      } catch (error) {
        if (!active() || error.name === 'AbortError') return;
        const target = append ? pagingStatus : results;
        target.replaceChildren(
          ctx.e('p', 'form-error', ctx.uiText(error.message)),
          ctx.button('Try again', () => void load(append, restore), 'secondary-button'),
        );
      } finally {
        if (!restorationQueued) releaseGestures();
        if (active()) {
          paging = false;
          results.setAttribute('aria-busy', 'false');
          if (completed) prefetch(current);
        }
      }
    }
    void load(false, true);
  }
  return { renderExplore, disposeExplore };
}
